export const APP_ENTRY_INTENTS = Object.freeze({
  MAGIC_LINK_ENTRY: 'magic_link_entry',
  EMAIL_LINK_ENTRY: 'email_link_entry',
  GOOGLE_REDIRECT_ENTRY: 'google_redirect_entry',
  OTP_ACTIVE_ENTRY: 'otp_active_entry',
  BUYER_GATEWAY_OUTBOUND_HANDOFF: 'buyer_gateway_outbound_handoff',
  BUYER_GATEWAY_X_CLOSE_OR_RETURN: 'buyer_gateway_x_close_or_return',
  BUYER_GATEWAY_PUBLIC_ENTRY: 'buyer_gateway_public_entry',
  PUBLIC_MAIN_ENTRY: 'public_main_entry',
  PUBLIC_LEGAL_ROUTE: 'public_legal_route',
  PROTECTED_WORKSPACE_ENTRY: 'protected_workspace_entry',
  TRUE_PWA_WORKSPACE_RESUME: 'true_pwa_workspace_resume',
  SIGNED_OUT_COLD_OPEN: 'signed_out_cold_open',
  UNKNOWN_ENTRY: 'unknown_entry',
});

export const BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY = 'cc_buyer_gateway_outbound_handoff_v1';
export const BUYER_GATEWAY_OUTBOUND_HANDOFF_TTL_MS = 15 * 60 * 1000;
export const BUYER_GATEWAY_OUTBOUND_NONCE_MAX_LENGTH = 80;

const BUYER_GATEWAY_HOSTS = new Set([
  'buyer.cassavachain.com',
  'www.buyer.cassavachain.com',
]);

function safeReadJson(storage, key) {
  try {
    const raw = storage?.getItem?.(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function safeWriteJson(storage, key, value) {
  try { storage?.setItem?.(key, JSON.stringify(value)); } catch (_) {}
}

function safeRemove(storage, key) {
  try { storage?.removeItem?.(key); } catch (_) {}
}

function nowMs() {
  return Date.now();
}

function cleanText(value, max = 160) {
  return String(value || '').trim().slice(0, max);
}

export function normalizeBuyerGatewayOutboundNonce(value) {
  if (typeof value !== 'string') return '';
  const nonce = value.trim();
  if (!nonce || nonce.length > BUYER_GATEWAY_OUTBOUND_NONCE_MAX_LENGTH) return '';
  return nonce;
}

export function isBuyerGatewayHost(hostname = '') {
  return BUYER_GATEWAY_HOSTS.has(String(hostname || '').trim().toLowerCase());
}

export function isBuyerGatewayUrl(url = '') {
  try {
    return isBuyerGatewayHost(new URL(String(url || ''), 'https://cassavachain.com').hostname);
  } catch (_) {
    return false;
  }
}

function normalizeSourceContext(value = '') {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'protected_workspace') return 'protected_workspace';
  if (raw === 'public_login') return 'public_login';
  if (raw === 'public_legal') return 'public_legal';
  if (raw === 'public_main') return 'public_main';
  return 'unknown';
}

function sourceContextIsPublic(value = '') {
  const context = normalizeSourceContext(value);
  return context === 'public_login' || context === 'public_main' || context === 'public_legal' || context === 'unknown';
}

function normalizeOutboundHandoff(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const targetHost = cleanText(payload.targetHost).toLowerCase();
  if (!isBuyerGatewayHost(targetHost)) return null;
  const nonce = normalizeBuyerGatewayOutboundNonce(payload.nonce);
  if (!nonce) return null;
  const startedAt = Number(payload.startedAt || payload.at || 0) || 0;
  const expiresAt = Number(payload.expiresAt || 0) || 0;
  const fresh = !!startedAt && (!expiresAt || nowMs() <= expiresAt) && nowMs() - startedAt <= BUYER_GATEWAY_OUTBOUND_HANDOFF_TTL_MS;
  if (!fresh) return null;
  return {
    intent: APP_ENTRY_INTENTS.BUYER_GATEWAY_OUTBOUND_HANDOFF,
    targetHost,
    sourceHost: cleanText(payload.sourceHost).toLowerCase(),
    sourcePath: cleanText(payload.sourcePath || '/'),
    sourceView: cleanText(payload.sourceView),
    sourceContext: normalizeSourceContext(payload.sourceContext),
    startedAt,
    expiresAt: expiresAt || startedAt + BUYER_GATEWAY_OUTBOUND_HANDOFF_TTL_MS,
    nonce,
    runtimeEpoch: Number(payload.runtimeEpoch || 0) || 0,
    version: 1,
  };
}

export function clearBuyerGatewayOutboundHandoff() {
  if (typeof window === 'undefined') return;
  safeRemove(window.sessionStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY);
  safeRemove(window.localStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY);
}

export function readBuyerGatewayOutboundHandoff({ consume = false } = {}) {
  if (typeof window === 'undefined') return null;
  const payload =
    normalizeOutboundHandoff(safeReadJson(window.sessionStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY)) ||
    normalizeOutboundHandoff(safeReadJson(window.localStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY));
  if (!payload) {
    clearBuyerGatewayOutboundHandoff();
    return null;
  }
  if (consume) clearBuyerGatewayOutboundHandoff();
  return payload;
}

export function writeBuyerGatewayOutboundHandoff({
  targetUrl = 'https://buyer.cassavachain.com',
  sourceContext = 'unknown',
  sourceView = '',
  runtimeEpoch = 0,
} = {}) {
  if (typeof window === 'undefined') return null;
  let targetHost = '';
  try {
    targetHost = new URL(String(targetUrl || ''), window.location?.origin || 'https://cassavachain.com').hostname;
  } catch (_) {
    targetHost = '';
  }
  if (!isBuyerGatewayHost(targetHost)) return null;
  const startedAt = nowMs();
  const payload = {
    intent: APP_ENTRY_INTENTS.BUYER_GATEWAY_OUTBOUND_HANDOFF,
    targetHost,
    sourceHost: cleanText(window.location?.hostname || '').toLowerCase(),
    sourcePath: cleanText(`${window.location?.pathname || '/'}${window.location?.search || ''}`),
    sourceView: cleanText(sourceView),
    sourceContext: normalizeSourceContext(sourceContext),
    startedAt,
    expiresAt: startedAt + BUYER_GATEWAY_OUTBOUND_HANDOFF_TTL_MS,
    nonce: `${startedAt}-${Math.random().toString(36).slice(2, 10)}`,
    runtimeEpoch: Number(runtimeEpoch || 0) || 0,
    version: 1,
  };
  safeWriteJson(window.sessionStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY, payload);
  safeWriteJson(window.localStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY, payload);
  return payload;
}

export function consumeBuyerGatewayReturnFromOutboundHandoff() {
  const handoff = readBuyerGatewayOutboundHandoff({ consume: true });
  if (!handoff) return null;
  return {
    intent: APP_ENTRY_INTENTS.BUYER_GATEWAY_X_CLOSE_OR_RETURN,
    source: 'buyer_gateway_outbound_handoff',
    sourceContext: handoff.sourceContext,
    sourcePath: handoff.sourcePath,
    sourceView: handoff.sourceView,
    targetHost: handoff.targetHost,
    startedAt: handoff.startedAt,
    runtimeEpoch: handoff.runtimeEpoch,
    publicReturn: sourceContextIsPublic(handoff.sourceContext),
    protectedReturn: handoff.sourceContext === 'protected_workspace',
    handoff,
  };
}

export function classifyAppEntryIntent({
  href = '',
  magicLinkActive = false,
  emailLinkActive = false,
  googleRedirectActive = false,
  otpActive = false,
  buyerGatewayReturn = false,
  buyerGatewayOutboundHandoff = null,
  publicLegalRoute = false,
  explicitPublicRoute = false,
  protectedWorkspaceHint = false,
  pwaStandalone = false,
  signedOutSettled = false,
} = {}) {
  const rawHref = String(href || '');
  if (magicLinkActive || /oobCode=|emailLink=1|mode=signIn/i.test(rawHref)) return { intent: APP_ENTRY_INTENTS.MAGIC_LINK_ENTRY, reason: 'magic-link-entry' };
  if (emailLinkActive) return { intent: APP_ENTRY_INTENTS.EMAIL_LINK_ENTRY, reason: 'email-link-entry' };
  if (googleRedirectActive) return { intent: APP_ENTRY_INTENTS.GOOGLE_REDIRECT_ENTRY, reason: 'google-redirect-entry' };
  if (otpActive) return { intent: APP_ENTRY_INTENTS.OTP_ACTIVE_ENTRY, reason: 'otp-active-entry' };
  if (buyerGatewayReturn) return { intent: APP_ENTRY_INTENTS.BUYER_GATEWAY_PUBLIC_ENTRY, reason: 'buyer-gateway-explicit-return' };
  const handoff = buyerGatewayOutboundHandoff || readBuyerGatewayOutboundHandoff({ consume: false });
  if (handoff) {
    return {
      intent: APP_ENTRY_INTENTS.BUYER_GATEWAY_X_CLOSE_OR_RETURN,
      reason: 'buyer-gateway-x-close-or-return',
      sourceContext: handoff.sourceContext,
      publicReturn: sourceContextIsPublic(handoff.sourceContext),
      protectedReturn: handoff.sourceContext === 'protected_workspace',
      handoff,
    };
  }
  if (publicLegalRoute) return { intent: APP_ENTRY_INTENTS.PUBLIC_LEGAL_ROUTE, reason: 'public-legal-route' };
  if (explicitPublicRoute) return { intent: APP_ENTRY_INTENTS.PUBLIC_MAIN_ENTRY, reason: 'public-main-entry' };
  if (pwaStandalone && protectedWorkspaceHint) return { intent: APP_ENTRY_INTENTS.TRUE_PWA_WORKSPACE_RESUME, reason: 'true-pwa-workspace-resume' };
  if (protectedWorkspaceHint) return { intent: APP_ENTRY_INTENTS.PROTECTED_WORKSPACE_ENTRY, reason: 'protected-workspace-entry' };
  if (signedOutSettled) return { intent: APP_ENTRY_INTENTS.SIGNED_OUT_COLD_OPEN, reason: 'signed-out-cold-open' };
  return { intent: APP_ENTRY_INTENTS.UNKNOWN_ENTRY, reason: 'unknown-entry' };
}

export function isBuyerGatewayPublicEntryIntent(intent = '') {
  return intent === APP_ENTRY_INTENTS.BUYER_GATEWAY_PUBLIC_ENTRY ||
    intent === APP_ENTRY_INTENTS.BUYER_GATEWAY_X_CLOSE_OR_RETURN;
}
